import { Validator } from "vee-validate";
import ImageFileCompression from "@/commons/utils/ImageFileCompression";
import PdfUtil, { PdfUtilResult, PdfUtilResultStatus } from "@/commons/utils/PdfUtil";
import { getType } from "mime";

/**
 * ### メッセージ送信共通ビジネスロジック
 *
 * - メッセージ送信登録画面の共通処理を切り出したクラス
 */
export default class SendMessageFormCommon {
  /** バリデーター */
  private validator: Validator;
  /** バリデーターで使用するフィールド名 */
  private fieldName: string;

  /**
   * ### コンストラクター
   *
   * @param validator バリデーション用クラス
   * @param fieldName バリデーションで使用するフィールド名
   */
  constructor(validator: Validator, fieldName: string) {
    this.validator = validator;
    this.fieldName = fieldName;
  }

  /**
   * ### 登録対象のメッセージ送信ファイルを作成する
   *
   * - PDFを優先ソートしてからファイルを読み込む
   * - エラーがあった場合、無効な操作として空の配列を返す
   *
   * @param files 登録対象候補のファイル
   */
  public async createAddFile(files: Array<File>, limit: number, currentFileCount: number, convertPdf: boolean): Promise<Array<ExtDraggableFile>> {
    // async処理でPromiseメソッドを実行しないと画面表示が崩れるので空のPromiseメソッドを実行する
    await this.emptyProsess();

    // 現在のバリデーションエラーをリセットしてから、バリデーションを実施する
    this.validator.errors.remove(this.fieldName);

    // 追加可能な残ファイル数を計算し、追加不可ならエラーメッセージを登録して即終了
    // 追加可能な残ファイル数は後続処理でも「あと何ファイル追加できるか」の計算で使用する
    let canAddFileCount: number = limit - currentFileCount;
    if (canAddFileCount < 1) {
      this.validator.errors.add({ field: this.fieldName, msg: `添付ファイル最大登録ファイル数以上は登録できません` });
      return [];
    }

    // PDFを優先ソートしてからファイルを読み込む
    const sortedFiles: Array<File> = this.sortFiles(files.slice());

    // 親コンポーネントに渡すファイルを作成する
    // PDFの場合は1ページを1枚のjpegに変換して、メッセージ送信1枚分として扱う
    const addFiles: Array<ExtDraggableFile> = [];

    for (const file of sortedFiles) {
      // ファイルのメディアタイプチェック
      // APIと同じライブラリでチェックすることで整合性を担保している
      const mime: string | null = this.getType(file.name);
      if (mime == null || mime != file.type) {
        const errorMessage: string = `${file.name}はjpg、mp4、mov、pdf以外の拡張子のため登録できません`;
        this.validator.errors.add({ field: this.fieldName, msg: errorMessage });
        return [];
      }

      // 追加可能かチェック
      if (canAddFileCount < 1) {
        this.validator.errors.add({ field: this.fieldName, msg: `添付ファイル最大登録ファイル数以上は登録できません` });
        return [];
      }

      // pdfの場合は全ページをjpeg化し、Fileに変換してkeyを作って配列に追加する
      if (file.type == "application/pdf") {
        if (convertPdf == true) {
          const pdf: PdfUtilResult = await PdfUtil.getPdfDocument(file);

          // 読込んだPDFをバリデーションしてNGなら空配列を返す
          const pdfValid: boolean = this.pdfValidation(pdf);
          if (!pdfValid || pdf.data == undefined) {
            return [];
          }

          for (let i = 1; i <= pdf.data.numPages; i++) {
            // 追加可能かチェック
            if (canAddFileCount < 1) {
              this.validator.errors.add({ field: this.fieldName, msg: `添付ファイル最大登録ファイル数以上は登録できません` });
              return [];
            }

            const jpeg: PdfUtilResult = await PdfUtil.convertPdfDocumentToJpeg(pdf.data, pdf.name, i);
            if (jpeg.dataUrl) {
              const byteString = atob(jpeg.dataUrl.split(",")[1]);
              const l = byteString.length;
              const content = new Uint8Array(l);
              for (let j = 0; l > j; j++) {
                content[j] = byteString.charCodeAt(j);
              }
              const addFile: File = new File([content], jpeg.name + ".jpeg", { type: "image/jpeg" });
              addFiles.push({
                name: jpeg.name + ".jpeg",
                type: "new",
                fileObject: addFile
              });
              canAddFileCount--;
            }
          }

          // 使用したPDFJSドキュメントを削除する
          pdf.data.destroy();
        } else {
          // ファイルサイズチェック
          // サイズを取得して比較し、規定サイズ以下ならエラー
          // 表示はmbyteにするが、サイズ比較はbyteで計算したいので、1024*1024*引数値とする
          const fileSize: number = 1024 * 1024 * 50;
          if (fileSize < file.size) {
            const errorMessage: string = `${file.name}のファイルサイズが50MBを超えているため登録できません`;
            this.validator.errors.add({ field: this.fieldName, msg: errorMessage });
            return [];
          }

          addFiles.push({
            name: file.name,
            type: "new",
            fileObject: file
          });
        }

        canAddFileCount--;
      }
      // jpegの場合はkeyを作成して配列に追加する
      else if (file.type == "image/jpeg") {
        // LINE APIの仕様に合わせるため、画像サイズが大きい場合は事前に圧縮する
        const compressedFile: File | undefined = await this.compressImage(file);
        if (!compressedFile) {
          continue;
        }

        addFiles.push({
          name: file.name,
          type: "new",
          fileObject: compressedFile
        });

        canAddFileCount--;
      }
      // mp4の場合はサイズチェック
      else if (file.type == "video/mp4") {
        // ファイルサイズチェック
        // サイズを取得して比較し、規定サイズ以下ならエラー
        // 表示はmbyteにするが、サイズ比較はbyteで計算したいので、1024*1024*引数値とする
        const fileSize: number = 1024 * 1024 * 200;
        if (fileSize < file.size) {
          const errorMessage: string = `${file.name}のファイルサイズが200MBを超えているため登録できません`;
          this.validator.errors.add({ field: this.fieldName, msg: errorMessage });
          return [];
        } else {
          addFiles.push({
            name: file.name,
            type: "new",
            fileObject: file
          });

          canAddFileCount--;
        }
      }
      // movの場合はサイズチェック
      else if (file.type == "video/quicktime") {
        // ファイルサイズチェック
        // サイズを取得して比較し、規定サイズ以下ならエラー
        // 表示はmbyteにするが、サイズ比較はbyteで計算したいので、1024*1024*引数値とする
        const fileSize: number = 1024 * 1024 * 200;
        if (fileSize < file.size) {
          const errorMessage: string = `${file.name}のファイルサイズが200MBを超えているため登録できません`;
          this.validator.errors.add({ field: this.fieldName, msg: errorMessage });
          return [];
        } else {
          addFiles.push({
            name: file.name,
            type: "new",
            fileObject: file
          });

          canAddFileCount--;
        }
      }
      // PDFまたはjpeg、mp4以外のファイルの場合はエラーとして扱う
      else {
        const errorMessage: string = `${file.name}はjpg、mp4、mov、pdf以外の拡張子のため登録できません`;
        this.validator.errors.add({ field: this.fieldName, msg: errorMessage });
        return [];
      }
    }

    return addFiles;
  }

  public async emptyProsess(): Promise<void> {
    return new Promise((reject: Function) => {
      reject();
    });
  }

  /**
   * ## ファイルのパスまたは拡張子からContent-Typeを取得する
   */
  public getType(fileString: string): string | null {
    /**
     * getTypeの引数にはファイルパスが必要
     * 先頭文字列が「.」の場合、拡張子でありファイルパスではないため、先頭にファイル名を付けてファイルパスにする
     * 先頭文字列が「.」ではない場合、ファイルパスとする
     */
    let contentType: string | null = "";
    if (fileString[0] == ".") {
      contentType = getType(`file${fileString}`);
    } else {
      contentType = getType(`${fileString}`);
    }

    return contentType;
  }

  /**
   * ### ファイルのソート
   *
   * - アプリ仕様により、pdfは優先的に前にする
   * - その他はファイル名でソートする
   */
  public sortFiles(files: Array<File>): Array<File> {
    const sortedFiles: Array<File> = files.sort((a: File, b: File) => {
      const nameA: string = a.name;
      const typeA: string = a.type;

      const nameB: string = b.name;
      const typeB: string = b.type;

      // aがpdfの場合、aが先
      if (typeA == "application/pdf" && typeB != "application/pdf") {
        return -1;
      }
      // bがpdfの場合、bが先
      if (typeA != "application/pdf" && typeB == "application/pdf") {
        return 1;
      }
      // a、bが両方pdfまたは両方pdfでない場合、名前順でソート
      if (nameA > nameB) {
        return 1;
      } else {
        return -1;
      }
    });

    return sortedFiles;
  }

  /**
   * ### 画像ファイルのサイズを圧縮する
   */
  public async compressImage(file: File): Promise<File | undefined> {
    const compressedFile: File | undefined = await ImageFileCompression.imageCompression(file, "image/jpeg", 1 * 1024 * 1024, 4096, 4096);

    return compressedFile ? compressedFile : undefined;
  }

  /**
   * ### PDFのバリデーション
   */
  public pdfValidation(pdf: PdfUtilResult): boolean {
    if (pdf.status == PdfUtilResultStatus.NO_ERROR) {
      // 変換したPDFのデータがない場合は非対応PDFとして扱う
      if (!pdf.data) {
        this.validator.errors.add({
          field: this.fieldName,
          msg: `${pdf.name}は非対応PDFのため登録できません`
        });

        return false;
      }

      return true;
    }
    // パスワード付きPDFの場合はエラー
    else if (pdf.status == PdfUtilResultStatus.LOCKED) {
      this.validator.errors.add({
        field: this.fieldName,
        msg: `${pdf.name}はパスワード付きPDFのため登録できません`
      });

      return false;
    }
    // 非対応PDFの場合はエラー
    else {
      this.validator.errors.add({
        field: this.fieldName,
        msg: `${pdf.name}は非対応PDFのため登録できません`
      });

      return false;
    }
  }
}

export interface ExtDraggableFile {
  /** 一意キー */
  key?: string;
  /** ファイル名 */
  name: string;
  /**
   * S3URL
   * 編集画面など、S3アップロードしたファイルでまだファイル取得していない状態の場合は格納される
   * 登録画面など、ファイルがアップロードされていない画面ではundefined
   */
  url?: string;
  /** 新規で入力されたファイルか、既存のファイルか */
  type: "new" | "existing";
  /**
   * ファイル
   * 編集画面など、S3アップロードしたファイルでまだファイル取得していない状態の場合はundefined
   * 登録画面など、ファイルがアップロードされていない画面では値が格納される
   */
  fileObject?: File;
}
